import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Button,
  Breadcrumb,
  Row,
  Col,
  Typography,
  Tag,
  Spin,
  Alert,
  Drawer,
  message,
  Table,
  Input,
  Space,
  Select,
  Form,
  Tabs,
} from "antd";
import {
  ArrowLeftOutlined,
  HomeOutlined,
  UserOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  PhoneOutlined,
  TeamOutlined,
  FileTextOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { FixedSizeList as List } from 'react-window';
// import { customerAPIWithErrorHandling } from "../../../api/customer/customer";
import debounce from "lodash/debounce";
import { groupCustomerAPIWithErrorHandling } from "../../../api/groupCustomer/groupCustomer";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import UploadModal from "../../../components/Upload/UploadModal";
import { customerAPIWithErrorHandling } from "../../../api/customer/customer";
import { settings } from "../../../utils/config";
import { neutral, spacing } from "../../../theme/tokens";
import StatCard from "../../../components/common/StatCard";
import RowActions from "../../../components/common/RowActions";
import { confirmAction } from "../../../components/common/confirmDialog";
import { border, radius, shadow } from "../../../theme/tokens";

const { Title, Text } = Typography;

const containerStyle = {
  backgroundColor: "#F5F7FA",
  minHeight: "100vh",
  padding: "15px",
};

const headerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: "24px",
  padding: "24px",
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  border: "1px solid #F1F5F9",
  flexWrap: "wrap",
  gap: "16px",
};

const userInfoStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flex: 1,
  minWidth: "250px",
};

const titleStyle = {
  marginBottom: "4px",
  color: "#1E293B",
  fontWeight: "600",
  fontSize: "24px",
};

const codeStyle = {
  fontSize: "14px",
  color: "#64748B",
};

const codeValueStyle = {
  color: "#667eea",
  fontWeight: "600",
  backgroundColor: "#f0f4ff",
  padding: "2px 8px",
  borderRadius: "4px",
};

const cardStyle = {
  boxShadow: shadow.xs,
  borderRadius: radius.md,
  border: border.hairline,
  overflow: "hidden",
};

/** Số tiền hiển thị dạng 1,234,567 */
const formatAmount = (value) =>
  `${value ?? 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Custom Hook để quản lý table data
const useTableData = (fetchFunction, organizationId) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(
    async (params = {}) => {
      if (!organizationId) return;

      try {
        setLoading(true);
        const response = await fetchFunction(organizationId, {
          page: params.page || pagination.current,
          per_page: params.per_page || pagination.pageSize,
          search: params.search !== undefined ? params.search : searchText,
        });

        if (response.success) {
          const { data: responseData } = response;
          setData(responseData.data);
          setPagination({
            current: responseData.current_page,
            pageSize: params.per_page || pagination.pageSize,
            total: responseData.total,
          });
        }
      } catch (error) {
        message.error(error.message || "Không thể tải dữ liệu");
      } finally {
        setLoading(false);
      }
    },
    [fetchFunction, organizationId, pagination.current, pagination.pageSize, searchText]
  );

  const handleSearch = useCallback(
    (value) => {
      setSearchText(value);
      loadData({ search: value, page: 1 });
    },
    [loadData]
  );

  const handleTableChange = useCallback(
    (newPagination) => {
      loadData({
        page: newPagination.current,
        per_page: newPagination.pageSize,
      });
    },
    [loadData]
  );

  return {
    data,
    loading,
    pagination,
    searchText,
    setData,
    loadData,
    handleSearch,
    handleTableChange,
  };
};

// Component Row cho react-window
const BulkCustomerRow = ({ index, style, data }) => {
  const customer = data.customers[index];
  const isSelected = data.selectedIds.includes(customer.id);
  
  const handleToggle = () => {
    if (isSelected) {
      data.setSelectedIds(data.selectedIds.filter(id => id !== customer.id));
    } else {
      data.setSelectedIds([...data.selectedIds, customer.id]);
    }
  };

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #F1F5F9',
        backgroundColor: isSelected ? '#F0F6FD' : 'white',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
      }}
      onClick={handleToggle}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#F8FAFC';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}
    >
      <input
        type="checkbox"
        checked={isSelected}
        onChange={handleToggle}
        style={{ marginRight: '12px', cursor: 'pointer', width: '16px', height: '16px' }}
        onClick={(e) => e.stopPropagation()}
      />
      
      <div style={{ flex: '0 0 15%', paddingRight: '12px' }}>
        <Tag color="blue" style={{ fontWeight: 500 }}>
          {customer.code}
        </Tag>
      </div>
      
      <div style={{ flex: '0 0 35%', paddingRight: '12px' }}>
        <div style={{ fontWeight: 500, marginBottom: '2px' }}>{customer.name}</div>
        {customer.email && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {customer.email}
          </Text>
        )}
      </div>
      
      <div style={{ flex: '0 0 25%', paddingRight: '12px' }}>
        <Space size="small">
          <PhoneOutlined style={{ color: '#128555' }} />
          <Text>{customer.phone || '-'}</Text>
        </Space>
      </div>
      
      <div style={{ flex: '0 0 15%' }}>
        <Tag color={customer.status === true ? 'green' : 'default'}>
          {customer.status === true ? 'Hoạt động' : 'Ngừng'}
        </Tag>
      </div>
    </div>
  );
};

export default function GroupCustomerInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [groupCustomer, setGroupCustomer] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [activeTab, setActiveTab] = useState("customers");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isBulkAddModalVisible, setIsBulkAddModalVisible] = useState(false);
  const [addForm] = Form.useForm();

  // Customer search for adding (Select mode)
  const [customerOptions, setCustomerOptions] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  // Bulk add mode (Table mode)
  const [bulkCustomers, setBulkCustomers] = useState([]);
  const [allBulkCustomers, setAllBulkCustomers] = useState([]);
  const [loadingBulkCustomers, setLoadingBulkCustomers] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [bulkSearchText, setBulkSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);

  // Stats
  const [totalCollections, setTotalCollections] = useState({
    total_paid: 0,
    total_unpaid: 0,
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Sử dụng custom hook cho customers
  const customersTable = useTableData(
    groupCustomerAPIWithErrorHandling.getCustomerByGroup,
    id
  );

  // Sử dụng custom hook cho collections
  const collectionsTable = useTableData(
    groupCustomerAPIWithErrorHandling.getGroupCollections,
    id
  );

  // Fetch organization info
  const fetchGroupCustomerInfo = async (groupId) => {
    if (!groupId) {
      message.error("ID nhóm khách hàng không hợp lệ");
      return;
    }

    setLoadingInfo(true);
    try {
      const response =
        await groupCustomerAPIWithErrorHandling.getGroupCustomerById(groupId);
      const groupCustomerData = response.data;
      setGroupCustomer(groupCustomerData);
    } catch (error) {
      message.error(error.message || "Không thể tải thông tin");
      console.error("Error fetching:", error);
    } finally {
      setLoadingInfo(false);
    }
  };

  // Fetch total collections
  const fetchTotalCollection = async (groupId) => {
    try {
      setLoadingStats(true);
      const response =
        await groupCustomerAPIWithErrorHandling.getTotalCollections(groupId);
      const priceData = response.data || {};
      setTotalCollections(priceData);
    } catch (error) {
      message.error("Không thể tải thông tin công nợ: " + error.message);
    } finally {
      setLoadingStats(false);
    }
  };

  // Debounced search customers for quick add
  const searchCustomers = useMemo(
    () =>
      debounce(async (searchValue) => {
        if (!searchValue || searchValue.length < 2) {
          setCustomerOptions([]);
          return;
        }

        setSearchingCustomers(true);
        try {
          const response = await organizationAPIWithErrorHandling.getCustomerAllByOrg(groupCustomer?.organization?.id, { search: searchValue });

          const customersData = response?.data || [];

          // Lọc ra những khách hàng chưa có trong org
          const currentCustomerIds = new Set(
            customersTable.data.map((c) => c.id)
          );
          const availableCustomers = customersData.filter(
            (c) => !currentCustomerIds.has(c.id)
          );

          setCustomerOptions(
            availableCustomers.map((item) => ({
              value: item.id,
              label: `${item.name} - ${item.phone || "N/A"} - ${item.code}`,
            }))
          );
        } catch (error) {
          message.error("Lỗi tìm kiếm khách hàng");
        } finally {
          setSearchingCustomers(false);
        }
      }, 500),
    [customersTable.data]
  );

  // Fetch customers for bulk add (react-window mode)
  const fetchBulkCustomers = async () => {
    setLoadingBulkCustomers(true);
    try {
      const response = await organizationAPIWithErrorHandling.getCustomerAllByOrg(groupCustomer?.organization?.id);
      const customersData = response?.data || [];
      const currentCustomerIds = new Set(customersTable.data.map((c) => c.id));

      // Lọc ra những khách hàng chưa có trong org
      const availableCustomers = customersData.filter(
        (c) => !currentCustomerIds.has(c.id)
      );

      setAllBulkCustomers(availableCustomers); // Lưu danh sách gốc
      setBulkCustomers(availableCustomers); // Hiển thị
    } catch (error) {
      message.error("Không thể tải danh sách khách hàng");
    } finally {
      setLoadingBulkCustomers(false);
    }
  };

  // Handle bulk search
  const handleBulkSearch = (value) => {
    setBulkSearchText(value);
    
    if (!value) {
      setBulkCustomers(allBulkCustomers);
      return;
    }
    
    const searchLower = value.toLowerCase();
    const filtered = allBulkCustomers.filter((customer) =>
      customer.name?.toLowerCase().includes(searchLower) ||
      customer.code?.toLowerCase().includes(searchLower) ||
      customer.phone?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
    
    setBulkCustomers(filtered);
  };

  // Handle add customer (Quick add - single/multiple via Select)
  // const handleAddCustomer = () => {
  //   setCustomerOptions([]);
  //   setIsAddModalVisible(true);
  // };

  // Handle bulk add (react-window mode)
  const handleBulkAdd = () => {
    setSelectedCustomerIds([]);
    setBulkSearchText("");
    setIsBulkAddModalVisible(true);
    fetchBulkCustomers();
  };

  // Submit add customer (Quick mode)
  const handleAddSubmit = async () => {
    try {
      const values = await addForm.validateFields();

      // Hỗ trợ chọn nhiều khách hàng
      const customerIds = Array.isArray(values.customer_id)
        ? values.customer_id
        : [values.customer_id];

      const payload = {
        customer_ids: customerIds,
      };

      await groupCustomerAPIWithErrorHandling.attachCustomers(id, payload);

      message.success(
        `Thêm thành công ${customerIds.length} khách hàng!`
      );
      setIsAddModalVisible(false);
      addForm.resetFields();
      customersTable.loadData();

      fetchGroupCustomerInfo(id);
      fetchTotalCollection(id);

    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error("Không thể thêm khách hàng: " + error.message);
    }
  };

  // Submit bulk add (react-window mode)
  const handleBulkAddSubmit = async () => {
    if (selectedCustomerIds.length === 0) {
      message.warning("Vui lòng chọn ít nhất 1 khách hàng!");
      return;
    }

    try {
      const payload = {
        customer_ids: selectedCustomerIds,
      };

      await groupCustomerAPIWithErrorHandling.attachCustomers(id, payload);

      message.success(
        `Thêm thành công ${selectedCustomerIds.length} khách hàng!`
      );
      setIsBulkAddModalVisible(false);
      setSelectedCustomerIds([]);
      customersTable.loadData();
      
      fetchGroupCustomerInfo(id);
      fetchTotalCollection(id);
    } catch (error) {
      message.error("Không thể thêm khách hàng: " + error.message);
    }
  };

  // Handle remove customer with optimistic update
  // Xác nhận trước khi gỡ — dùng chung hộp thoại của hệ thống thay cho Popconfirm
  const confirmRemoveCustomer = async (record) => {
    const confirmed = await confirmAction({
      title: `Gỡ "${record.name || ""}" khỏi nhóm?`,
      content: "Khách hàng không bị xóa khỏi hệ thống.",
      okText: "Gỡ",
      okType: "danger",
    });
    if (!confirmed) return;

    handleRemoveCustomer(record.id);
  };

  const handleRemoveCustomer = async (customerId) => {
    // Optimistic update
    const previousData = [...customersTable.data];

    customersTable.setData((prev) => prev.filter((c) => c.id !== customerId));

    try {
      const payload = {
        customer_ids: [customerId],
      };

      await groupCustomerAPIWithErrorHandling.detachCustomers(id, payload);
      message.success("Xóa khách hàng khỏi nhóm khách hàng thành công!");

      // Reload để có data chính xác
      customersTable.loadData();
      
      fetchGroupCustomerInfo(id);
      fetchTotalCollection(id);
    } catch (error) {
      // Rollback on error
      customersTable.setData(previousData);
      message.error("Không thể xóa khách hàng: " + error.message);
    }
  };

  // Handle tab change
  const handleTabChange = (key) => {
    setActiveTab(key);

    // Lazy load collections khi chuyển sang tab
    if (key === "collections" && collectionsTable.data.length === 0) {
      collectionsTable.loadData();
    }
  };

  const handleUploadSuccess = async ({ files}) => {
    try {
      if (!files || files.length === 0) {
        message.warning('Vui lòng chọn ít nhất 1 file để upload!');
        return;
      }


      // FIXED: Lấy file gốc đúng cách
      const file = files[0].originFileObj;
      
      if (!file) {
        message.error('Không tìm thấy file để upload!');
        return;
      }

       await customerAPIWithErrorHandling.importCustomers(file, [], id);

      message.success('Tải lên thành công, dữ liệu đang được xử lý!');

      // Đóng modal
      setModalVisible(false);
      customersTable.loadData();
      fetchGroupCustomerInfo(id);
      fetchTotalCollection(id);

    } catch (error) {
      console.error('Upload error:', error);
      message.error('Tải lên thất bại, vui lòng thử lại!');
    }
  };

  const handleDownloadSampleFile = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_URL;
      
      const exportUrl = baseUrl ? `${baseUrl}/api/v1/datamanagement/sample-customers` : `/api/v1/datamanagement/sample-customers`;
      console.log('exportUrl:', baseUrl, exportUrl)

      const hasAccessToken = settings?.getCookie("access_token");
      // Gọi API tải file mẫu
      const response = await fetch(exportUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${hasAccessToken}`,
        }
      });
      
      if (!response.ok) throw new Error('Lỗi tải file mẫu!');
      
      // Tạo blob từ response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Tạo link download
      const link = document.createElement('a');
      link.href = url;
      link.download = 'template_tai_lieu.xlsx';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      throw error; // Component sẽ hiển thị lỗi
    }
  };

  useEffect(() => {
    if (id) {
      fetchGroupCustomerInfo(id);
      customersTable.loadData();
      fetchTotalCollection(id);
    }
  }, [id]);

  const handleBack = () => {
    navigate("/group-customers");
  };

  // Customer Table columns
  const customerColumns = [
    {
      title: "STT",
      key: "index",
      width: "10%",
      fixed: "left",
      render: (_, record, index) =>
        (customersTable.pagination.current - 1) *
          customersTable.pagination.pageSize +
        index +
        1,
    },
    {
      title: "Mã KH",
      key: "code",
      width: "15%",
      render: (_, record) => (
        <Tag color="blue" style={{ fontWeight: 500 }}>
          {record.code}
        </Tag>
      ),
    },
    {
      title: "Khách hàng",
      key: "name",
      ellipsis: true,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: "4px" }}>
            {record.name || `Khách hàng ${record.code}`}
          </div>
        </div>
      ),
    },
    {
      title: "Số điện thoại",
      key: "phone",
      width: "15%",
      render: (_, record) => (
        <Space>
          <PhoneOutlined style={{ color: "#128555" }} />
          <Text>{record.phone || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Trạng thái",
      key: "status",
      width: "15%",
      render: (_, record) => (
        <Tag color={record.status === true ? "green" : "default"}>
          {record.status === true ? "Hoạt động" : "Ngừng"}
        </Tag>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <RowActions
          actions={[
            {
              key: "info", label: "Xem chi tiết", icon: <InfoCircleOutlined />, tone: "view",
              onClick: () => navigate(`/customers/information/${record.id}`),
            },
            {
              key: "detach", label: "Gỡ khỏi nhóm", icon: <DeleteOutlined />, tone: "danger",
              onClick: () => confirmRemoveCustomer(record),
            },
          ]}
        />
      ),
    },
  ];
  
  const handleCusInfo = (record) => {
      message.info(`Chi tiết thông tin khách hàng: ${record.customer?.name}`);
      navigate(`/customers/information/${record.customer_id}`);
  };

  // Collections Table columns
  const collectionColumns = [
    {
      title: "STT",
      key: "index",
      width: "5%",
      render: (_, record, index) =>
        (collectionsTable.pagination.current - 1) *
          collectionsTable.pagination.pageSize +
        index +
        1,
    },
    {
      title: "Mã HD",
      key: "code",
      width: "10%",
      render: (_, record) => <Tag>{record.code}</Tag>,
    },
    {
      title: "Khách hàng",
      key: "customer",
      width: "18%",
      render: (_, record) => (
        <div onClick={() => handleCusInfo(record)}
            style={{
                cursor: "pointer",
                color: "#0070D7",
            }}>
          <div style={{ fontWeight: 500 }}>
            {record.customer?.name || "_"}
          </div>
          
          {record.customer?.phone && (
            <Text type="secondary" style={{ fontSize: "13px" }}>
              {record.customer?.phone}
            </Text>
          )}
        </div>
      ),
    },
    {
      // Tài sản (số nhà) gắn với khoản thu. Ưu tiên tên tài sản đang lưu, thiếu thì lấy
      // charge_target_number ghi thẳng trên khoản thu — khoản thu loại CUSTOMER hoặc dữ
      // liệu cũ không có asset_id nên không phải dòng nào cũng có tài sản.
      title: "Tài sản",
      key: "asset",
      width: "13%",
      render: (_, record) => {
        const tenTaiSan = record.asset?.name || record.charge_target_number;

        if (!tenTaiSan) {
          return <Text type="secondary">_</Text>;
        }

        return (
          <div>
            <div style={{ fontWeight: 500 }}>{tenTaiSan}</div>
            {record.asset?.code && (
              <Text type="secondary" className="text-xs">
                {record.asset.code}
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: "Đợt thu",
      key: "collection",
      width: "17%",
      render: (_, record) => (
        <div style={{ fontWeight: 500 }}>{record.collection?.name || "_"}</div>
      ),
    },
    {
      title: "Tổng cộng",
      key: "total",
      width: "13%",
      align: "right",
      render: (_, record) => {
        const total = Number(record.amount_due || 0);
        return (
          <Text strong style={{ color: "#0070D7" }}>
            {total.toLocaleString("vi-VN")} ₫
          </Text>
        );
      },
    },
    {
      title: "Đã thanh toán",
      key: "amount_paid",
      width: "12%",
      align: "right",
      render: (_, record) => {
        const total = Number(record.amount_paid || 0);
        return (
          <Text strong style={{ color: "#A8681E" }}>
            {total.toLocaleString("vi-VN")} ₫
          </Text>
        );
      },
    },
    {
      title: "Trạng thái",
      key: "status",
      width: "15%",
      render: (_, record) => {
        const statusLabels = {
          PAID: "Đã thanh toán",
          CANCELLED: "Đã hủy",
          UNPAID: "Chưa thanh toán",
          PARTIAL: "Một phần",
        };

        const statusColors = {
          PAID: "green",
          CANCELLED: "red",
          UNPAID: "orange",
          PARTIAL: "blue",
        };

        return (
          <Tag color={statusColors[record.status] || "default"}>
            {statusLabels[record.status] || record.status}
          </Tag>
        );
      },
    },
  ];

  if (loadingInfo && !groupCustomer) {
    return (
      <div
        style={{
          ...containerStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Spin size="large" tip="Đang tải thông tin nhóm khách hàng..." />
      </div>
    );
  }

  if (!groupCustomer) {
    return (
      <div style={containerStyle}>
        <Alert
          message="Không tìm thấy thông tin"
          description="Nhóm khách hàng có thể đã bị xóa hoặc ID không hợp lệ."
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={handleBack}>
              Quay lại danh sách
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Breadcrumb */}
      <Breadcrumb
        separator=">"
        style={{ marginBottom: "14px" }}
        items={[
          {
            href: "/",
            title: (
              <>
                <HomeOutlined /> Trang chủ
              </>
            ),
          },
          {
            href: "/organizations",
            title: (
              <>
                <TeamOutlined /> Nhóm khách hàng
              </>
            ),
          },
          {
            title: (
              <>
                <InfoCircleOutlined /> Chi tiết
              </>
            ),
          },
        ]}
      />

      {/* Header */}
      <div style={headerStyle}>
        <div style={userInfoStyle}>
          <div style={{ flex: 1 }}>
            <Title level={2} style={titleStyle}>
              {groupCustomer.name}
            </Title>
            <Text type="secondary" style={codeStyle}>
              Mã nhóm khách hàng:{" "}
              <span style={codeValueStyle}>{groupCustomer.code}</span>
            </Text>{" "}
            <Text type="secondary" style={codeStyle}>
              Đơn vị trực thuộc:{" "}
              <span style={codeValueStyle}>{groupCustomer?.organization?.name}</span>
            </Text>
          </div>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack} size="default">
          Quay lại
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: "24px" }}>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<TeamOutlined />}
            tone="brand"
            label="Tổng khách hàng"
            value={customersTable.pagination.total}
            loading={customersTable.loading}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<CheckCircleOutlined />}
            tone="success"
            label="Đã thanh toán"
            value={formatAmount(totalCollections.total_paid)}
            loading={loadingStats}
          />
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <StatCard
            icon={<ClockCircleOutlined />}
            tone="warning"
            label="Chưa thanh toán"
            value={formatAmount(totalCollections.total_unpaid)}
            loading={loadingStats}
          />
        </Col>
      </Row>

      {/* Tabs Card */}
      <Card style={cardStyle}>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            {
              key: "customers",
              label: (
                <Space>
                  <UserOutlined />
                  <span>
                    Khách hàng
                  </span>
                </Space>
              ),
              children: (
                <>
                  <Space
                    style={{
                      marginBottom: 16,
                      width: "100%",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                    }}
                  >
                    <Input.Search
                      placeholder="Tìm kiếm khách hàng..."
                      allowClear
                      onSearch={customersTable.handleSearch}
                      style={{ width: 300 }}
                      prefix={<SearchOutlined />}
                    />
                    <Space>
                      {/* <Button
                        type="default"
                        icon={<PlusOutlined />}
                        onClick={handleAddCustomer}
                      >
                        Thêm nhanh
                      </Button> */}
                      <Button
                        type="primary"
                        icon={<TeamOutlined />}
                        onClick={handleBulkAdd}
                      >
                        Thêm hàng loạt
                      </Button>
                      <Button
                        icon={<UploadOutlined />}
                        onClick={() => setModalVisible(true)}
                      >
                        Import
                      </Button>
                    </Space>
                  </Space>
                  <Table
                    columns={customerColumns}
                    dataSource={customersTable.data}
                    rowKey="id"
                    loading={customersTable.loading}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    pagination={{
                      ...customersTable.pagination,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} khách hàng`,
                      pageSizeOptions: ["10", "15", "20", "50"],
                    }}
                    onChange={customersTable.handleTableChange}
                  />
                </>
              ),
            },
            {
              key: "collections",
              label: (
                <Space>
                  <FileTextOutlined />
                  <span>
                    Phiếu thu
                  </span>
                </Space>
              ),
              children: (
                <>
                  <Space
                    style={{
                      marginBottom: 16,
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Input.Search
                      placeholder="Tìm kiếm phiếu thu..."
                      allowClear
                      onSearch={collectionsTable.handleSearch}
                      style={{ width: 300 }}
                      prefix={<SearchOutlined />}
                    />
                  </Space>
                  <Table
                    columns={collectionColumns}
                    dataSource={collectionsTable.data}
                    rowKey="id"
                    loading={collectionsTable.loading}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    pagination={{
                      ...collectionsTable.pagination,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} của ${total} phiếu thu`,
                      pageSizeOptions: ["10", "15", "20", "50"],
                    }}
                    onChange={collectionsTable.handleTableChange}
                  />
                </>
              ),
            },
          ]}
        />
      </Card>

      {/* Quick Add Customer Drawer */}
      <Drawer
        title={
          <Space>
            <PlusOutlined style={{ color: "#667eea" }} />
            <span>Thêm nhanh khách hàng</span>
          </Space>
        }
        placement="right"
        width="min(560px, 100vw)"
        open={isAddModalVisible}
        onClose={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
          setCustomerOptions([]);
        }}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button
              onClick={() => {
                setIsAddModalVisible(false);
                addForm.resetFields();
                setCustomerOptions([]);
              }}
            >
              Hủy
            </Button>
            <Button type="primary" onClick={handleAddSubmit}>
              Thêm
            </Button>
          </div>
        }
      >
        <Form form={addForm} layout="vertical" style={{ marginTop: "24px" }}>
          <Form.Item
            name="customer_id"
            label="Chọn khách hàng"
            rules={[{ required: true, message: "Vui lòng chọn khách hàng!" }]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="Nhập tên hoặc SĐT để tìm kiếm (tối thiểu 2 ký tự)..."
              onSearch={searchCustomers}
              loading={searchingCustomers}
              filterOption={false}
              notFoundContent={
                searchingCustomers ? (
                  <Spin size="small" />
                ) : customerOptions.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "20px" }}>
                    <Text type="secondary">
                      Nhập tên hoặc SĐT để tìm kiếm
                    </Text>
                  </div>
                ) : null
              }
              options={customerOptions}
              suffixIcon={<SearchOutlined />}
              maxTagCount="responsive"
            />
          </Form.Item>

          <Alert
            message="Lưu ý"
            description={
              <ul style={{ margin: "4px 0", paddingLeft: "10px" }}>
                <li>Có thể chọn nhiều khách hàng cùng lúc. Tìm kiếm bằng tên hoặc số điện thoại</li>
                <li>
                  Dùng <strong>"Thêm hàng loạt"</strong> nếu muốn xem danh sách
                  đầy đủ
                </li>
              </ul>
            }
            type="info"
            showIcon
          />
        </Form>
      </Drawer>

      {/* Bulk Add Customer Drawer with react-window */}
      <Drawer
        title={
          <Space>
            <TeamOutlined style={{ color: "#667eea" }} />
            <span>
              Thêm khách hàng hàng loạt
              {selectedCustomerIds.length > 0 && (
                <Tag color="blue" style={{ marginLeft: 8 }}>
                  Đã chọn: {selectedCustomerIds.length}
                </Tag>
              )}
            </span>
          </Space>
        }
        placement="right"
        width="min(880px, 100vw)"
        open={isBulkAddModalVisible}
        onClose={() => {
          setIsBulkAddModalVisible(false);
          setSelectedCustomerIds([]);
          setBulkSearchText("");
        }}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button
              onClick={() => {
                setIsBulkAddModalVisible(false);
                setSelectedCustomerIds([]);
                setBulkSearchText("");
              }}
            >
              Hủy
            </Button>
            <Button
              type="primary"
              onClick={handleBulkAddSubmit}
              disabled={selectedCustomerIds.length === 0}
            >
              {`Thêm ${selectedCustomerIds.length} khách hàng`}
            </Button>
          </div>
        }
      >
        <div style={{ marginTop: "16px" }}>
          <Space
            style={{
              marginBottom: 16,
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Input.Search
              placeholder="Tìm kiếm khách hàng..."
              allowClear
              onSearch={handleBulkSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  handleBulkSearch('');
                }
              }}
              style={{ width: 400 }}
              prefix={<SearchOutlined />}
            />
            <Space>
              <Button
                size="small"
                onClick={() => setSelectedCustomerIds(bulkCustomers.map(c => c.id))}
              >
                Chọn tất cả
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedCustomerIds([])}
              >
                Bỏ chọn
              </Button>
              <Text type="secondary">
                {bulkCustomers.length} khách hàng
              </Text>
            </Space>
          </Space>

          {/* Header */}
          <div
            style={{
              display: 'flex',
              padding: '12px 16px',
              backgroundColor: '#F8FAFC',
              borderBottom: '2px solid #F1F5F9',
              fontWeight: 600,
              border: '1px solid #F1F5F9',
              borderRadius: '8px 8px 0 0',
            }}
          >
            <div style={{ width: '40px' }}></div>
            <div style={{ flex: '0 0 15%', paddingRight: '12px' }}>Mã KH</div>
            <div style={{ flex: '0 0 35%', paddingRight: '12px' }}>Tên khách hàng</div>
            <div style={{ flex: '0 0 25%', paddingRight: '12px' }}>Số điện thoại</div>
            <div style={{ flex: '0 0 15%' }}>Trạng thái</div>
          </div>

          {/* Virtual List */}
          {loadingBulkCustomers ? (
            <div style={{ textAlign: 'center', padding: '40px', border: '1px solid #F1F5F9', borderTop: 'none' }}>
              <Spin tip="Đang tải danh sách khách hàng..." />
            </div>
          ) : bulkCustomers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', border: '1px solid #F1F5F9', borderTop: 'none' }}>
              <Text type="secondary">
                {bulkSearchText ? 'Không tìm thấy khách hàng phù hợp' : 'Không có khách hàng nào'}
              </Text>
            </div>
          ) : (
            <div style={{ border: '1px solid #F1F5F9', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
              <List
                height={400}
                itemCount={bulkCustomers.length}
                itemSize={70}
                width="100%"
                itemData={{
                  customers: bulkCustomers,
                  selectedIds: selectedCustomerIds,
                  setSelectedIds: setSelectedCustomerIds,
                }}
              >
                {BulkCustomerRow}
              </List>
            </div>
          )}

          <Alert
            message="Lưu ý"
            description="Danh sách chỉ hiển thị khách hàng chưa thuộc nhóm khách hàng này. Bạn có thể chọn nhiều khách hàng và thêm cùng lúc."
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      </Drawer>

      {modalVisible &&
        <UploadModal
          group={true}
          visible={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={handleUploadSuccess}
          title="Upload Tài Liệu"
          acceptedFileTypes=".xlsx,.xls,.csv"
          maxFileSize={10}
          showSampleDownload={true}
          sampleFileName="template_tai_lieu.xlsx"
          onDownloadSample={handleDownloadSampleFile}
        />
      }
    </div>
  );
}